import { NotificationRoutingComponent } from '@shared/components/notification-routing/notification-routing';
import { SidePanel } from '@shared/components/side-panel/side-panel';
import { CampaignWorkflowStore, CampaignTab } from '@features/campaigns/store/campaign-workflow.store';
import { CampaignRunDetail } from '../../components/campaign-run-detail/campaign-run-detail';
import { CampaignRunPanel } from '@features/campaigns/components/campaign-run-panel/campaign-run-panel';
import { CampaignAnalysesTab } from '@features/campaigns/components/campaign-analyses-tab/campaign-analyses-tab';
import { StageChip } from '@features/campaigns/components/stage-chip/stage-chip';
import {
  Component,
  ChangeDetectionStrategy,
  OnDestroy,
  inject,
  computed,
  effect,
  signal,
  TemplateRef,
  ViewChild,
  untracked,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { HlmButtonDirective } from '@spartan-ng/helm/button';
import { HlmInputDirective } from '@spartan-ng/helm/input';
import { HlmDialogImports } from '@spartan-ng/helm/dialog';
import { BrnDialogImports } from '@spartan-ng/brain/dialog';
import { map, Subscription } from 'rxjs';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { NgIconComponent, provideIcons } from '@ng-icons/core';
import { lucideEllipsis } from '@ng-icons/lucide';
import { CampaignStatus, PACKAGE_TYPE_METADATA } from '@features/campaigns/models/campaign.model';
import { HlmMenuImports } from '@spartan-ng/helm/menu';
import { BrnMenuTriggerDirective } from '@spartan-ng/brain/menu';

import { CampaignStore } from '@features/campaigns/store/campaign-store';
import { AnalysisStore } from '@features/campaigns/store/analysis-store';
import { AddressService } from '@shared/services/address.service';
import { CurrentOrganizationStore } from '@features/organizations/store/current-organization.store';
import { CampaignsService } from '@features/campaigns/services/campaigns.service';
import { NotificationStore } from '@shared/stores/notification.store';
import { CampaignOverviewTab } from '@features/campaigns/components/campaign-overview-tab/campaign-overview-tab';
import { CampaignResultReviewTab } from '@features/campaigns/components/campaign-result-review-tab/campaign-result-review-tab';
import { CampaignPlanningResultsTab } from '@features/campaigns/components/campaign-planning-results-tab/campaign-planning-results-tab';
import { CampaignExecutionsTab } from '@features/campaigns/components/campaign-executions-tab/campaign-executions-tab';
import { HeaderActionsService } from '@shared/services/header-actions.service';
import { PageHeaderMetaService } from '@shared/services/page-header-meta.service';
import { PageTabsService } from '@shared/services/page-tabs.service';
import { InlineNotice } from '@shared/components/inline-notice/inline-notice';
import { StatePanel } from '@shared/components/state-panel/state-panel';
import { ToastService } from '@shared/services/toast.service';
import { ConfirmationDialogService } from '@shared/services/confirmation-dialog.service';

const TABS: CampaignTab[] = ['overview', 'analyses', 'executions', 'review', 'plannings'];

@Component({
  selector: 'app-campaign-detail',
  standalone: true,
  imports: [
    NotificationRoutingComponent,
    CommonModule,
    FormsModule,
    HlmButtonDirective,
    HlmInputDirective,
    ...HlmDialogImports,
    ...BrnDialogImports,
    NgIconComponent,
    BrnMenuTriggerDirective,
    ...HlmMenuImports,
    CampaignOverviewTab,
    CampaignResultReviewTab,
    CampaignPlanningResultsTab,
    CampaignExecutionsTab,
    InlineNotice,
    StatePanel,
    SidePanel,
    CampaignRunPanel,
    CampaignRunDetail,
    CampaignAnalysesTab,
    StageChip,
  ],
  providers: [
    CampaignWorkflowStore,
    provideIcons({
      lucideEllipsis,
    }),
  ],
  templateUrl: './campaign-detail.html',
  styleUrl: './campaign-detail.css',
  // No box of its own: full-bleed tabs have to reach the layout's height.
  host: { class: 'contents' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CampaignDetail implements OnDestroy {
  readonly editOpen = signal(false);
  readonly editTitle = signal('');
  readonly editDescription = signal('');
  readonly isSaving = signal(false);
  readonly editError = signal<string | null>(null);

  openEdit(): void {
    this.editTitle.set(this.campaign()?.title ?? '');
    this.editDescription.set(this.campaign()?.description ?? '');
    this.editError.set(null);
    this.editOpen.set(true);
  }

  async saveDetails(): Promise<void> {
    const campaign = this.campaign();
    const orgId = this.organizationId();
    if (!campaign || !orgId || !this.editTitle().trim() || this.isSaving()) return;
    this.isSaving.set(true);
    this.editError.set(null);
    try {
      await this.campaignsService.updateCampaign(orgId, campaign.id, {
        title: this.editTitle().trim(), description: this.editDescription().trim(),
      });
      await this.campaignStore.loadCampaign(orgId, campaign.id);
      this.editOpen.set(false);
    } catch {
      this.editError.set('Die Änderungen konnten nicht gespeichert werden. Bitte erneut versuchen.');
    } finally {
      this.isSaving.set(false);
    }
  }

  private readonly route = inject(ActivatedRoute);
  private readonly campaignStore: CampaignStore = inject(CampaignStore);
  private readonly analysisStore: AnalysisStore = inject(AnalysisStore);
  private readonly addressService: AddressService = inject(AddressService);
  readonly currentOrgStore: CurrentOrganizationStore = inject(CurrentOrganizationStore);
  private readonly campaignsService: CampaignsService = inject(CampaignsService);
  private readonly notificationStore = inject(NotificationStore);
  private readonly headerActions = inject(HeaderActionsService);
  private readonly pageHeaderMeta = inject(PageHeaderMetaService);
  private readonly pageTabs = inject(PageTabsService);
  readonly workflow = inject(CampaignWorkflowStore);
  private readonly router = inject(Router);
  private readonly toast = inject(ToastService);
  private readonly confirmation = inject(ConfirmationDialogService);
  readonly notificationsOpen = signal(false);
  private readonly headerControlsTemplate = signal<TemplateRef<unknown> | null>(null);
  private readonly campaignId = toSignal(
    this.route.paramMap.pipe(map((params) => params.get('id'))),
    { initialValue: this.route.snapshot.paramMap.get('id') },
  );
  private loadedContext: string | null = null;

  @ViewChild('campaignHeaderControls', { static: true })
  set campaignHeaderControls(template: TemplateRef<unknown>) {
    this.headerControlsTemplate.set(template);
  }

  private setupEventsSubscription: Subscription | null = null;
  private setupPollInterval: ReturnType<typeof setInterval> | null = null;

  readonly campaign = this.campaignStore.currentCampaign;
  readonly isLoading = this.campaignStore.isLoading;
  readonly hasError = this.campaignStore.hasError;
  readonly errorMessage = this.campaignStore.errorMessage;
  readonly organizationId = computed(() => this.currentOrgStore.organization()?.id || null);

  readonly analysisCount = computed(() => this.analysisStore.pagination().total);

  readonly activeTab = this.workflow.tab;
  /** The picture is the work on this tab, so it runs to the card's edges. */
  readonly fullBleed = computed(() => this.activeTab() === 'review');

  constructor() {
    const wanted = this.route.snapshot.queryParamMap.get('tab') as CampaignTab | null;
    if (wanted && TABS.includes(wanted)) this.workflow.tab.set(wanted);

    effect(() => {
      const organizationId = this.organizationId();
      const campaignId = this.campaignId();
      if (!organizationId || !campaignId) return;

      const context = `${organizationId}:${campaignId}`;
      if (this.loadedContext === context) return;
      this.loadedContext = context;

      void this.campaignStore.loadPackageConfig(organizationId);
      void this.loadCampaignData(organizationId, campaignId);
      this.workflow.init(organizationId, campaignId);
    });
    effect(() => {
      const campaign = this.campaign();
      this.pageHeaderMeta.set(campaign ? {
        title: campaign.title,
        description: campaign.description || undefined,
        actionsTemplate: this.headerControlsTemplate(),
        fullBleed: this.fullBleed(),
        titleActions: this.currentOrgStore.can('campaign', 'update')
          ? [{ label: 'Kampagne bearbeiten', icon: 'lucidePencil', iconOnly: true, onClick: () => this.openEdit() }]
          : [],
        badges: [
          { label: 'Paket', value: campaign.packageType ? PACKAGE_TYPE_METADATA[campaign.packageType]?.label ?? campaign.packageType : 'Kein Paket' },
          { label: 'Erstellt', value: new Date(campaign.createdAt).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }), monospace: true },
          { label: 'Analysen', value: this.analysisCount().toLocaleString('de-DE'), monospace: true },
        ],
      } : null);
      this.headerActions.set([]);
    });
    effect(() => {
      const counts = this.workflow.counts();
      const review = counts ? counts.review + counts.seen + counts.flagged : undefined;
      this.pageTabs.set(
        [
          { value: 'overview', label: 'Übersicht' },
          { value: 'analyses', label: 'Analysen', count: this.workflow.total() || undefined },
          { value: 'executions', label: 'Berechnungen', count: this.workflow.running() ? counts?.running || undefined : undefined },
          { value: 'review', label: 'Ergebnisse prüfen', count: review || undefined },
          { value: 'plannings', label: 'Planungsergebnisse', count: counts?.done || undefined },
        ],
        this.activeTab(),
        (tab) => void this.workflow.go(tab as CampaignTab),
      );
    });
    // The tab stays in the URL, so a reload or a shared link opens it again.
    effect(() => {
      const tab = this.activeTab();
      untracked(() => void this.router.navigate([], { relativeTo: this.route, queryParams: { tab: tab === 'overview' ? null : tab }, queryParamsHandling: 'merge', replaceUrl: true }));
    });
  }

  async canDeactivate(): Promise<boolean> {
    return (await this.workflow.leaveGuard?.()) ?? true;
  }

  async exportCampaign(format: 'csv' | 'excel'): Promise<void> {
    const orgId = this.organizationId();
    const campaign = this.campaign();
    if (!orgId || !campaign) return;
    try {
      await this.campaignsService.exportCampaign(orgId, campaign.id, format);
    } catch {
      this.toast.error('Export fehlgeschlagen.');
    }
  }

  async archive(): Promise<void> {
    const orgId = this.organizationId();
    const campaign = this.campaign();
    if (!orgId || !campaign) return;
    const ok = await this.confirmation.confirm({
      title: 'Kampagne archivieren?',
      description: 'Die Kampagne verschwindet aus den aktiven Kampagnen. Ihre Analysen bleiben erhalten.',
      confirmText: 'Archivieren',
      cancelText: 'Abbrechen',
      type: 'warning',
    });
    if (ok) await this.campaignStore.updateCampaignStatus(orgId, campaign.id, CampaignStatus.ARCHIVED);
  }

  ngOnDestroy(): void {
    this.workflow.destroy();
    this.setupEventsSubscription?.unsubscribe();
    this.stopSetupPolling();
  }

  private async loadCampaignData(organizationId: string, campaignId: string): Promise<void> {
    await this.campaignStore.loadCampaign(organizationId, campaignId);

    if (!this.campaign()) return;

    const isSettingUp = !this.campaign()!.campaignSiId && !this.campaign()!.metadata?.['setupFailed'];
    if (isSettingUp) {
      this.subscribeToSetupEvents(organizationId, campaignId);
    }
    this.analysisStore.loadAllAnalysesByCampaign(organizationId, campaignId);
    this.preloadAddresses();
  }

  private async preloadAddresses(): Promise<void> {
    const orgId = this.organizationId();
    if (orgId) {
      try {
        await this.addressService.getAddressesByOrganization(orgId);
      } catch (error) {
        console.warn('[CampaignDetail] Address preloading failed (non-critical):', error);
      }
    }
  }

  private subscribeToSetupEvents(orgId: string, campaignId: string): void {
    if (this.setupEventsSubscription) return;

    this.startSetupPolling(orgId, campaignId);

    const slug = this.currentOrgStore.organization()?.slug;
    const campaignTitle = this.campaign()?.title ?? 'Kampagne';
    const notifId = this.notificationStore.addProgress(
      `„${campaignTitle}" wird eingerichtet`,
      slug ? `/organizations/${slug}/campaigns/${campaignId}` : undefined,
    );

    this.setupEventsSubscription = this.campaignsService
      .watchSetupEvents(orgId, campaignId)
      .subscribe({
        next: (event) => {
          if (event.type === 'complete') {
            this.notificationStore.updateProgress(notifId, {
              status: 'complete',
              body: `${(event as any).analysesCreated ?? ''} Analysen eingerichtet`.trim(),
            });
            this.stopSetupPolling();
            void this.reloadAfterSetup(orgId, campaignId);
          } else if (event.type === 'failed') {
            this.notificationStore.updateProgress(notifId, {
              status: 'failed',
              body: (event as any).error ?? 'Einrichtung fehlgeschlagen',
            });
            this.stopSetupPolling();
            void this.reloadAfterSetup(orgId, campaignId);
          }
        },
        error: () => {
          this.notificationStore.updateProgress(notifId, { body: 'Verbindung unterbrochen. Der Status wird im Hintergrund erneut geprüft.' });
          this.setupEventsSubscription = null;
        },
        complete: () => {
          this.setupEventsSubscription = null;
          this.stopSetupPolling();
          void this.reloadAfterSetup(orgId, campaignId);
        },
      });
  }

  /**
   * Once SI setup finishes, the analyses and addresses it created did not exist
   * during the initial page load. Clear the analysis prefetch cache (which still
   * holds the empty pre-setup page) and refetch campaign, analyses and addresses
   * so the table populates without a manual refresh.
   */
  private async reloadAfterSetup(orgId: string, campaignId: string): Promise<void> {
    this.analysisStore.clearCache();
    await this.campaignStore.loadCampaign(orgId, campaignId);
    await this.analysisStore.loadAllAnalysesByCampaign(orgId, campaignId);
    await this.preloadAddresses();
  }

  private startSetupPolling(orgId: string, campaignId: string): void {
    if (this.setupPollInterval) return;
    this.setupPollInterval = setInterval(async () => {
      await this.campaignStore.loadCampaign(orgId, campaignId);
      const isSettingUp = !this.campaign()?.campaignSiId && !this.campaign()?.metadata?.['setupFailed'];
      if (!isSettingUp) {
        this.stopSetupPolling();
        await this.reloadAfterSetup(orgId, campaignId);
      }
    }, 10_000);
  }

  private stopSetupPolling(): void {
    if (this.setupPollInterval) {
      clearInterval(this.setupPollInterval);
      this.setupPollInterval = null;
    }
  }
}
